import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { Review, ReviewImg } from '@/js/services/reviews/reviewTypes'
import type { IRogue } from '@/js/services/RogueTypes'

export type ReviewsState = {
  reviews: {
    [sku: string]: Review[]
  },
  summaries: {
    [sku: string]: {
      count: number;
      average: number;
    }
  },
  reviewSummaries: {
    [sku: string]: string | undefined
  },
  gallery: {
    [sku: string]: {
      images: (ReviewImg & { review: Review })[],
      totalReviewsWithPhotos: number,
      loadedReviewsWithPhotos: number,
    }
  },
}

export const useReviewsStore = defineStore('reviews', () => {
  const summariesLoading: { [sku: string]: Promise<void> } = {}
  const reviewsLoading: { [sku: string]: Promise<void> } = {}

  const reviews = ref<ReviewsState['reviews']>({})
  const summaries = ref<ReviewsState['summaries']>({})
  const reviewSummaries = ref<ReviewsState['reviewSummaries']>({})
  const gallery = ref<ReviewsState['gallery']>({})

  async function loadFirstEightReviews(sku: string, rogue: IRogue) {
    if (!sku || !sku.length) {
      return
    }

    const skuIndex = sku.toUpperCase()
    if (reviews.value[skuIndex]?.length) {
      return
    }

    if (!reviewsLoading[skuIndex]) {
      reviewsLoading[skuIndex] = import(/* webpackChunkName: "reviews" */'@/js/services/reviews/bazaarvoice')
        .then(fns => fns.getReviews(sku, rogue, 0, 8))
        .then(results => {
          reviews.value[skuIndex] = results.reviews || []
          reviewSummaries.value[skuIndex] = results.aiSummary
          gallery.value[skuIndex] = {
            images                  : results.gallery?.images || [],
            totalReviewsWithPhotos  : results.gallery?.totalAvailable || 0,
            loadedReviewsWithPhotos : results.gallery?.totalLoaded || 0,
          }
          delete reviewsLoading[skuIndex]
        })
        .catch(err => {
          delete reviewsLoading[skuIndex]
          rogue.log.warn('Load first eight reviews failed', err)
        })
    }

    await reviewsLoading[skuIndex]
  }

  function setSummariesFromProducts(products: { sku: string; ratingCount?: number; ratingAverage?: number }[]) {
    products.forEach(p => {
      if (!p.sku || p.ratingCount == null) return

      const key = p.sku.toUpperCase()
      
      if (!summaries.value[key]) {
        summaries.value[key] = {
          count   : p.ratingCount || 0,
          average : Math.round((p.ratingAverage || 0) * 10) / 10,
        }
      }
    })
  }

  async function loadSummaries(skus: string[], rogue: IRogue) {
    skus = skus.filter(sku => !!sku && sku.length).map(sku => sku.toUpperCase())

    if (!skus.length) {
      return
    }
    /**
     * Sometimes, multiple review components will all want the same review. One might load ECHOBIKE,ECHO_WINDGUARD and another
     * might load just ECHOBIKE. So we want to wait for existing requests rather than make a new one
     */
    const promises: Promise<void>[] = skus
      .filter(sku => !summaries.value[sku] && summariesLoading[sku])
      .map(sku => summariesLoading[sku])

    // We will need to request any that arent loaded / loading
    const toLoad = skus.filter(sku => !summariesLoading[sku] && !summaries.value[sku])

    if (toLoad.length) {
      const promise = import('@/js/services/reviews/bazaarvoice')
        .then(fns => fns.getStats(toLoad, rogue))
        .then(results => results.forEach(reviewStat => {
          reviewStat.average = Math.round(reviewStat.average * 10) / 10 || 0
          summaries.value[reviewStat.sku.toUpperCase()] = { count: reviewStat.count || 0, average: reviewStat.average || 0 }
        }))
        // Remove the old promises
        .then(() => toLoad.forEach(sku => delete summariesLoading[sku]))

      // Wait for these SKUs to load
      promises.push(promise)

      // Set this promise as the "loading" promises for all the SKUs contained within, so any future calls will wait for the same request
      toLoad.forEach(sku => summariesLoading[sku] = promise)
    }

    if (promises.length) {
      await Promise.all(promises).catch(err => {
        rogue.log.warn('Reviews store failed', err)
      })
    }
  }

  return {
    reviews,
    summaries,
    reviewSummaries,
    gallery,
    loadFirstEightReviews,
    loadSummaries,
    setSummariesFromProducts,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReviewsStore, import.meta.hot))
}
